import { Badge, Spin } from "antd";
import styles from "./index.module.css";
import { useRequest } from "ahooks";
import { AdminContext } from "context/adminContext";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  blockAd,
  getGroupOptions,
  getVersionsByIdProduct,
  GetVersionsByIdProductType,
  updateGroupOfAdV2,
  updateProductStatus,
} from "helpers/backend_helper";
import { useParams } from "react-router";
import { NotificationContext } from "context/notificationContext";
import { Link, useSearchParams } from "react-router-dom";
import LockRedBold from "assets/svg/LockRedBold.svg";
import { CustomSegmented } from "Components/CustomSegmented";
import { ProductStatusEnum } from "helpers/types";
import { ActionsComponent } from "./ActionsComponent";
import { InfoComponent } from "Components/InfoComponent";
import { CarouselComponent } from "Components/CarouselComponent";
import { CascaderComponent } from "./CascaderComponent";
import { PriceComponent } from "./Price";
import { NotWorkingDaysComponent } from "./NotWorkingDaysComponent";
import ItemReviews from "../ItemComponent/Reviews";
import { FeaturesComponent } from "./FeaturesComponent";
import { getOptions } from "helpers/helper";
import { TabsComponent } from "Components/TabsComponent";
import { PROP_NAME_MAIN_TAB, PROP_NAME_NEXT_TAB, PROP_NAME_SKIP_ADS, PROPS_NAME_IS_NEW } from "helpers/constants";
import { TableDeals } from "../ItemComponent/TableDeals";
import { TablePayments } from "../ItemComponent/TablePayments";
import dayjs from "dayjs";
import { formatNumber, MATCHER_PRODUCT_STATUSES } from "./utils";
import caretRight from "assets/svg/CaretRight.svg";
import { DeclineModal } from "Components/DeclineModal";
import { CustomVideoBtn } from "Components/CustomVideoBtn";

const getItems = (itemId: string) => {
  return [
    {
      key: "0",
      label: (
        <Badge count={0} size="small" className={styles.badge}>
          <div className={styles.deals}>Deals</div>
        </Badge>
      ),
      children: <TableDeals itemId={itemId} />,
    },
    {
      key: "1",
      label: (
        <Badge count={0} size="small" className={styles.badge}>
          <div className={styles.deals}>Payments</div>
        </Badge>
      ),
      children: <TablePayments productId={itemId} />,
    },
  ];
};

export const ItemV2Component = () => {
  const { localCountry } = useContext(AdminContext);
  const { itemId } = useParams();
  const { openNotificationWithIcon } = useContext(NotificationContext);
  const [currentStatus, setCurrentStatus] = useState<ProductStatusEnum>(null);
  const [reviewCount, setReviewCount] = useState(0);
  const [data, setData] = useState<GetVersionsByIdProductType>(null);
  const [choosenData, setChoosenData] = useState<any>(null);
  const [isAdRestricted, setIsAdRestricted] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentGroupId, setCurrentGroupId] = useState<number>(null);
  const currentVersion = useRef<"current" | "draft">("current");
  const currentCategoryIds = useRef<{
    current: { productGroupId: number; categoryId: number; categoryIds: number[] };
    draft?: { productGroupId: number; categoryId: number; categoryIds: number[] };
  }>(null);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);

  const onCloseDeclineModal = () => setIsDeclineModalOpen(false);
  const onOpenDeclineModal = () => setIsDeclineModalOpen(true);

  const items = useMemo(() => getItems(itemId), [itemId]);

  const [categoriesValue, setCategoriesValue] = useState<number[]>([]);

  const { runAsync: updateProductStatusNew, loading: isProductUpdating } = useRequest(
    (status: ProductStatusEnum, comment?: string) =>
      updateProductStatus(
        itemId,
        {
          status,
          comment,
        },
        localCountry.iso.toUpperCase(),
      ),
    {
      manual: true,
      refreshDeps: [localCountry?.iso],
      ready: !!localCountry?.iso,
      onSuccess: (_, p) => {
        if (p[0]) setIsDeclineModalOpen(false);
        getVersions();
      },
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || error.message);
      },
    },
  );

  const { runAsync: updateProduct, loading: isProductUpdateLoading } = useRequest(
    (status: ProductStatusEnum, comment?: string) =>
      updateProductStatus(
        itemId,
        {
          status,
          comment,
        },
        localCountry.iso.toUpperCase(),
      ),
    {
      manual: true,
      refreshDeps: [localCountry?.iso],
      ready: !!localCountry?.iso,
      onSuccess: (_, p) => {
        if (p[0]) setIsDeclineModalOpen(false);
        getVersions();
      },
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || error.message);
      },
    },
  );

  useEffect(() => {
    if (!choosenData) return;

    setReviewCount(choosenData?.reviewCount);
    setCurrentGroupId(choosenData?.productGroupId);
    setCategoriesValue([...(choosenData?.categoryIds as number[]), choosenData?.productGroupId]);
  }, [choosenData]);

  useEffect(() => {
    if (!data) return;

    const isNew = searchParams.get(PROPS_NAME_IS_NEW);

    changeVersion(!isNew);
  }, [searchParams, data]);

  const changeVersion = (isOld: boolean) => {
    // eslint-disable-next-line no-debugger
    debugger;
    if (isOld) {
      setReviewCount(data.current.reviewCount);
      setCurrentGroupId(data.current.productGroupId);
      setChoosenData(data.current);
      setCurrentStatus(data.current.status);
      currentVersion.current = "current";

      searchParams.delete(PROPS_NAME_IS_NEW);
    } else {
      setReviewCount(data.draft.reviewCount);
      setCurrentGroupId(data.draft.productGroupId);
      setChoosenData(data.draft);
      setCurrentStatus(data.draft.status);
      currentVersion.current = "draft";
      searchParams.set(PROPS_NAME_IS_NEW, "true");
    }

    setSearchParams(searchParams);
  };

  const { runAsync: getVersions, loading } = useRequest(
    () => getVersionsByIdProduct(itemId, localCountry.iso.toUpperCase()),
    {
      manual: true,
      ready: !!localCountry?.iso,
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || error.message);
      },
      onSuccess: (data) => {
        setData(data);
        setCurrentStatus(data.current.status);
        currentCategoryIds.current = {
          current: {
            categoryId: data.current.categoryId,
            productGroupId: data.current.productGroupId,
            categoryIds: data.current.categoryIds,
          },
        };

        if (data.draft) {
          currentCategoryIds.current.draft = {
            categoryId: data.draft.categoryId,
            productGroupId: data.draft.productGroupId,
            categoryIds: data.draft.categoryIds,
          };
        }

        if (!choosenData || !data.draft) {
          setChoosenData(data.current);
          setIsAdRestricted(data.current.blocked);
          currentVersion.current = "current";
        } else {
          setChoosenData((prev) => ({
            ...prev,
            status: data.current.id === choosenData.id ? data.current.status : data.draft.status,
          }));
        }
      },
    },
  );

  const onReplacePhoto = ({
    index,
    isDraft,
    original,
    path,
  }: {
    original: string;
    path: string;
    index: number;
    isDraft: boolean;
  }) => {
    setData((v) => {
      if (isDraft) {
        const slicedPhotos = v.draft.photos.slice();

        slicedPhotos[index] = { original, path };

        v.draft.photos = slicedPhotos;
      } else {
        const slicedPhotos = v.current.photos.slice();

        slicedPhotos[index] = { original, path };

        v.current.photos = slicedPhotos;
      }
      return v;
    });
  };

  useEffect(() => {
    getVersions();
  }, []);

  const { data: groupOptions, loading: groupOptionsLoading } = useRequest(
    () => getGroupOptions(localCountry?.iso.toUpperCase(), currentGroupId),
    {
      ready: !!localCountry?.iso && !!currentGroupId,
      refreshDeps: [localCountry, currentGroupId],
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || error.message);
      },
    },
  );

  const { loading: isUpdatingGroupLoaidng, runAsync: updateGroup } = useRequest(
    (groupId: number, categoryId: number) =>
      updateGroupOfAdV2(
        itemId,
        { groupId, categoryId: categoryId },
        localCountry.iso.toUpperCase(),
        currentVersion?.current,
      ),
    {
      manual: true,
      refreshDeps: [localCountry],
      ready: !!localCountry?.iso && !!itemId,
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || error.message);
      },
      onSuccess: () => {
        openNotificationWithIcon("success", "Ads group has changed");
      },
    },
  );

  const onCategoryCancelChanges = () => {
    const productGroupId = currentCategoryIds.current[currentVersion?.current].productGroupId;
    const categoryIds = currentCategoryIds.current[currentVersion.current].categoryIds;
    const categoryId = currentCategoryIds.current[currentVersion.current].categoryId;
    setCurrentGroupId(currentCategoryIds.current[currentVersion?.current].productGroupId);
    setCategoriesValue([...categoryIds, productGroupId]);
    setChoosenData((v) => {
      return { ...v, categoryId, categoryIds, productGroupId };
    });
  };

  const onGroupIdChange = (ids: number[]) => {
    setData((prev) => {
      const newObj = { ...prev };

      if (currentVersion?.current === "current") {
        newObj.current.categoryIds = ids.slice(0, -1);
        newObj.current.categoryId = ids.at(-2);
        newObj.current.productGroupId = ids.at(-1);
      } else {
        newObj.draft.categoryIds = ids.slice(0, -1);
        newObj.draft.categoryId = ids.at(-2);
        newObj.draft.productGroupId = ids.at(-1);
      }

      return newObj;
    });
    setCategoriesValue(ids);
    setCurrentGroupId(ids.at(-1));
  };

  const featuresArray = useMemo(() => {
    if (!data || !groupOptions) return [];
    const existedOptions = choosenData?.productOption?.map((v) => getOptions(v));
    return groupOptions.options.map((opt) => {
      const exist = existedOptions.find((v) => v.id === opt.id);

      if (exist) return { name: opt.name, value: exist.value, id: exist.id };
      return { name: opt.name, value: null, id: opt.id };
    });
  }, [groupOptions, data]);

  const categoryHasChanged = useMemo(() => {
    if (!currentVersion?.current || !currentCategoryIds?.current) return false;
    return (
      choosenData?.categoryId !== currentCategoryIds?.current[currentVersion?.current].categoryId ||
      choosenData?.productGroupId !== currentCategoryIds?.current[currentVersion?.current].productGroupId
    );
  }, [choosenData?.categoryId, choosenData?.productGroupId, data]);

  const onPublishProduct = () => {
    searchParams.delete(PROPS_NAME_IS_NEW);
    setSearchParams(searchParams);
    updateProductStatusNew(ProductStatusEnum.PUBLISHED);
  };
  const onDeclineProduct = async (comment: string) => {
    if (categoryHasChanged) {
      await updateGroup(categoriesValue.at(-1), categoriesValue.at(-2));
    }
    return updateProduct(ProductStatusEnum.CORRECTIONS, comment);
  };

  const { runAsync: onBlockAd, loading: isBlockAdLoading } = useRequest(
    (block: boolean) => blockAd({ blocked: block, localCountry: localCountry.iso.toUpperCase(), productId: itemId }),
    {
      manual: true,
      refreshDeps: [localCountry],
      ready: !!localCountry?.iso && !!itemId,
      onError: (error: any) => {
        openNotificationWithIcon("error", error.response?.data?.message || error.message);
      },
      onSuccess: (_, p) => {
        setIsAdRestricted(p[0]);
      },
    },
  );
  return (
    <>
      <div className={styles.container}>
        {(loading && !data) || !choosenData ? (
          <Spin style={{ left: "50%", top: "50%", transform: "translate(50% -50%)" }} />
        ) : (
          <>
            <div className={styles.userChoosenTop}>
              <Link type="text" to={"/users"} replace style={{ padding: 0 }} className={styles.linkTop}>
                Users
              </Link>
              <img src={caretRight} alt="right arrow icon" width={16} height={16} />
              <Link
                type="text"
                to={{
                  pathname: "/users/user",
                  search: `?countryUserId=${data?.current?.userId}`,
                }}
                style={{ padding: 0 }}
                className={styles.linkTop}
              >
                {`${data?.current?.user?.firstName}`}
              </Link>
              <img src={caretRight} alt="right arrow icon" width={16} height={16} />
              <div style={{ fontWeight: 700 }}>Product ID {data?.current?.id}</div>
              <div style={{ margin: "0 auto" }}>
                {data?.draft ? (
                  <CustomSegmented
                    options={[
                      {
                        value: "old",
                        label: <span>Old, {dayjs(data?.current.createdAt).format("DD.MM.YYYY")}</span>,
                      },
                      {
                        value: "new",
                        label: <span>New</span>,
                      },
                    ]}
                    onChange={(v) => {
                      changeVersion(v !== "new");
                    }}
                    value={searchParams.get(PROPS_NAME_IS_NEW) ? "new" : "old"}
                  />
                ) : null}
              </div>
              <div style={{ display: "flex", gap: 5, margin: "0 0 0 auto" }}>
                <div style={choosenData?.qualityRate === undefined ? { visibility: "hidden" } : {}}>
                  <InfoComponent
                    infoOfData="Rate of insurance"
                    label="Quality Rate"
                    value={choosenData?.qualityRate}
                    className={styles.componentQuality}
                  />
                </div>
                <div style={choosenData?.qualityRate === undefined ? { visibility: "hidden" } : {}}>
                  <InfoComponent
                    infoOfData="Rate of insurance"
                    label="Usage Rate"
                    value={choosenData?.usagePercentage + "%"}
                    className={styles.componentQuality}
                  />
                </div>
              </div>
            </div>
            <div className={styles.mainInfo}>
              {loading ? (
                <Spin style={{ left: "20%", top: "50%", transform: "translate(50% -50%)" }} />
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ fontWeight: 700, fontSize: 24, lineHeight: "24px" }}>
                    {choosenData?.name ? choosenData?.name : "-"}
                    <span
                      style={{ fontWeight: 700, color: "#868686", fontSize: 24, marginLeft: 10, lineHeight: "110%" }}
                    >
                      {choosenData?.address}
                    </span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <div style={{ fontSize: 16, fontWeight: 700, marginRight: 10 }}>ID {itemId}</div>
                    {isAdRestricted ? (
                      <div className={styles.adResctricted}>
                        <img src={LockRedBold} alt="a lock" /> <span>Ad Restricted</span>
                      </div>
                    ) : null}
                  </div>
                </div>
              )}
              <div className={styles.infContainer}>
                <div className={styles.changeStatus}>
                  <span>Status</span>
                  <span style={{ fontWeight: 500 }}>{MATCHER_PRODUCT_STATUSES[choosenData?.status]}</span>
                  <ActionsComponent
                    isAdRestricted={isAdRestricted}
                    onConfirm={onPublishProduct}
                    onDecline={onOpenDeclineModal}
                    onBlock={() => onBlockAd(true)}
                    onUnBlock={() => onBlockAd(false)}
                    status={currentStatus}
                    isBlockButtonLoading={isBlockAdLoading}
                    isAllButtonLoading={isProductUpdating}
                    hasDraft={!!data.draft}
                  />
                </div>
              </div>
            </div>
            <div className={styles.infoWrapper}>
              <div className={styles.leftWrapper}>
                <CarouselComponent
                  photos={choosenData?.photos}
                  info={{ id: data?.current.id, version: currentVersion?.current, status: choosenData.status }}
                  onReplacePhoto={onReplacePhoto}
                />
                {choosenData?.video && (
                  <div style={{ marginTop: 22 }}>
                    <CustomVideoBtn videoLink={choosenData?.video} />
                  </div>
                )}
                <div className={styles.description}>{choosenData?.description}</div>
                <div style={{ marginTop: 36 }}>
                  {groupOptionsLoading || isUpdatingGroupLoaidng ? (
                    <Spin style={{ left: "50%", top: "50%", transform: "translate(50% -50%)" }} />
                  ) : featuresArray?.length ? (
                    <div style={{ marginBottom: 60 }}>
                      <FeaturesComponent features={featuresArray} />
                    </div>
                  ) : null}
                </div>
              </div>
              <div className={styles.rightWrapper}>
                <div style={{ flexDirection: "row" }}>
                  <div className={styles.priceLessor}>
                    <div>
                      <span>Lessor</span>{" "}
                      <Link
                        to={`/users/user?countryUserId=${data?.current?.userId}&isRenter=false`}
                        style={{
                          textDecoration: "underline",
                          textUnderlineOffset: "-0.15em",
                          textDecorationThickness: 1,
                        }}
                      >
                        {data?.current?.user.firstName} {data?.current?.user.lastName}
                      </Link>
                    </div>
                    <div className={styles.productCost}>
                      <span>Product Cost</span> <span>€{choosenData?.price}</span>
                    </div>
                  </div>
                  <div>
                    <CascaderComponent
                      value={categoriesValue}
                      onGroupIdChange={onGroupIdChange}
                      isDisabled={choosenData?.status !== ProductStatusEnum.PENDING}
                    />
                  </div>
                </div>
                <div>
                  <div className={styles.daysPriceWrapper}>
                    <PriceComponent
                      dayPrice={choosenData?.rentalDay ? +formatNumber(choosenData.rentalDay) : 0}
                      daysRange="2-7"
                    />
                    <PriceComponent
                      dayPrice={choosenData?.rentalWeek ? +formatNumber(choosenData.rentalWeek) : 0}
                      daysRange="8-30"
                      discount={
                        choosenData?.rentalWeek
                          ? ((choosenData?.rentalWeek / choosenData?.rentalDay) * 100).toFixed(1)
                          : null
                      }
                    />
                    <PriceComponent
                      dayPrice={choosenData?.rentalMonth ? +formatNumber(choosenData.rentalMonth) : 0}
                      daysRange="31+"
                      discount={
                        choosenData?.rentalMonth
                          ? ((choosenData?.rentalMonth / choosenData?.rentalDay) * 100).toFixed(1)
                          : null
                      }
                    />
                  </div>
                  <NotWorkingDaysComponent
                    dateRangeUnavailableDates={choosenData?.dateRangeUnavailableDates}
                    unavailableDates={choosenData?.unavailableDates}
                    unavailableWeekdays={choosenData?.unavailableWeekdays}
                  />
                </div>
                <div>
                  {reviewCount ? (
                    <ItemReviews
                      itemId={itemId}
                      reviewCount={reviewCount}
                      onReviewChangeCount={(n) => setReviewCount(n)}
                      rating={choosenData?.rating}
                    />
                  ) : (
                    <div className={styles.noReviewsYet}>No reviews yet</div>
                  )}
                </div>
              </div>
            </div>
            <TabsComponent
              defaultActiveKey={searchParams.get(PROP_NAME_MAIN_TAB) || "0"}
              items={items}
              className={styles.tabs}
              destroyInactiveTabPane
              onChange={(activeKey) => {
                searchParams.set(PROP_NAME_MAIN_TAB, items.find((v) => v.key === activeKey).key);
                searchParams.delete(PROP_NAME_SKIP_ADS);
                searchParams.delete(PROP_NAME_NEXT_TAB);
                setSearchParams(searchParams, { replace: true });
              }}
            />
          </>
        )}
      </div>
      <DeclineModal
        isLoading={isProductUpdateLoading}
        isView={false}
        isOpened={isDeclineModalOpen}
        onClose={onCloseDeclineModal}
        onSend={onDeclineProduct}
        isCategoryChanged={categoryHasChanged}
        cancelCategoryChanges={onCategoryCancelChanges}
      />
    </>
  );
};
